import { useEffect, useState } from 'react';

import useTranslation from '../../../features/locale/useTranslation';
import { formatDate } from '../../../utils';
import { type CalendarDateInputValue } from '../Calendar';

type PanelDatesPreviewProps = {
    startDate: CalendarDateInputValue;
    endDate: CalendarDateInputValue;
};

/**
 * This hook is used in the Calendar component to control which dates should be displayed in the preview.
 * That way we can show to the user a preview of the hovered panel item without actually selecting it
 * or triggering the `onChange` callback.
 */
const usePanelDatesPreview = ({ startDate, endDate }: PanelDatesPreviewProps) => {
    const { t } = useTranslation();

    const [start, setStart] = useState<CalendarDateInputValue>(startDate);
    const [end, setEnd] = useState<CalendarDateInputValue>(endDate);

    const updatePreview = (
        _start: CalendarDateInputValue | CalendarDateInputValue[],
        _end?: CalendarDateInputValue
    ) => {
        if (Array.isArray(_start)) {
            setStart(_start[0] ?? null);
            setEnd(_start[1] ?? null);
        } else {
            setStart(_start ?? null);
            setEnd(_end ?? null);
        }
    };

    const startDatePreviewLabel = start ? formatDate(start?.toISOString()) : t('start date');
    const endDatePreviewLabel = end ? formatDate(end?.toISOString()) : t('end date');

    useEffect(() => {
        updatePreview(startDate ?? null, endDate ?? null);
    }, [startDate, endDate]);

    return {
        startDatePreview: start,
        endDatePreview: end,
        startDatePreviewLabel,
        endDatePreviewLabel,
        updatePreview
    };
};

export default usePanelDatesPreview;
